import type { CurrencyBaseUnit } from '@shared/models/units/currency-units.model';
import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { Icon } from '@ui/Icon';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { PROJECT_HOVER_BACKGROUND } from '../../project-list.constants';

interface ProjectResultMetricsProps {
  co2: string;
  co2Diff?: number;
  cost: string;
  currency?: CurrencyBaseUnit;
  selected: boolean;
}

export const ProjectResultMetrics: React.FC<ProjectResultMetricsProps> = ({
  co2,
  co2Diff,
  cost,
  currency,
  selected,
}) => (
  <>
    <Box
      className="project-card-carbon"
      sx={{
        width: 96,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: 13,
        lineHeight: '16px',
        fontWeight: 500,
        color: selected ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.6)',
        whiteSpace: 'nowrap',
      }}
    >
      <Tooltip title="Carbon per m² (GFA)">
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Box
            component="span"
            sx={{ display: 'flex', color: 'rgba(0,0,0,0.6)' }}
          >
            <Icon category="costEmission" icon="kgCO2e" size={16} />
          </Box>
          {co2}
        </Box>
      </Tooltip>
      {co2Diff !== undefined && co2Diff !== 0 && (
        <Tooltip title="Carbon difference from active baseline">
          <Box
            component="span"
            data-project-carbon-diff=""
            sx={{
              marginLeft: '4px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '1px',
              padding: '0 4px',
              fontSize: 11,
              lineHeight: 'normal',
              minHeight: 15,
              borderRadius: '1px',
              backgroundColor:
                co2Diff < 0
                  ? '#c8ffd6'
                  : co2Diff > 0
                    ? '#fbe7ea'
                    : PROJECT_HOVER_BACKGROUND,
              color:
                co2Diff < 0
                  ? '#266b37'
                  : co2Diff > 0
                    ? '#803943'
                    : 'rgba(0,0,0,0.5)',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
            }}
          >
            {co2Diff !== 0 && (
              <Icon
                category="mui"
                icon={co2Diff < 0 ? 'ArrowDownward' : 'ArrowUpward'}
                size={12}
              />
            )}
            {getDisplayNumber(Math.abs(co2Diff), {
              notation: 'compact',
            })}
          </Box>
        </Tooltip>
      )}
    </Box>
    {currency && (
      <Tooltip title="Expenses per m² (GFA)">
        <Box
          className="project-card-cost"
          sx={{
            width: 56,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: selected ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
            fontSize: 13,
            lineHeight: '16px',
            whiteSpace: 'nowrap',
          }}
        >
          <Icon category="currency" icon={currency} size={16} />
          {cost}
        </Box>
      </Tooltip>
    )}
  </>
);
